/**
 * RepConnect - Healthcare Meeting Scheduling Platform
 *
 * Copyright (c) 2025 RepConnect, LLC
 * Website: meetwithreps.com
 *
 * This software and associated documentation files are the proprietary
 * property of RepConnect, LLC.
 * All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { Menu, Bell, Coffee } from 'lucide-react';
import AuthScreen from './components/AuthScreen';
import PasswordResetScreen from './components/PasswordResetScreen';
import OnboardingFlow from './components/OnboardingFlow';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import MobileDashboard from './components/MobileDashboard';
import MobileNavigation from './components/MobileNavigation';
import MobileBookingFlow from './components/MobileBookingFlow';
import MobileProviderSearch from './components/MobileProviderSearch';
import MobileCalendar from './components/MobileCalendar';
import ManagerDashboard from './components/ManagerDashboard';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import AdminManagement from './components/AdminManagement';
import AdminInviteAcceptance from './components/AdminInviteAcceptance';
import TeamManagement from './components/TeamManagement';
import InstallPWA from './components/InstallPWA';
import IOSInstallPrompt from './components/IOSInstallPrompt';
import { authService, supabase, offlineSyncManager } from './utils/supabase';
import { meetingService } from './utils/supabaseData';
import { PaymentUtils } from './utils/stripeConfig';
import PaywallPopup from './components/PaywallPopup';
import FindProvidersView from './views/FindProvidersView';
import BookMeetingView from './views/BookMeetingView';
import MeetingsView from './views/MeetingsView';
import AvailabilityView from './views/AvailabilityView';
import ComplianceReportsView from './views/ComplianceReportsView';
import MeetingHistoryView from './views/MeetingHistoryView';
import SettingsView from './views/SettingsView';
import CustomerLocationsView from './views/CustomerLocationsView';
import DiningPreferencesView from './views/DiningPreferencesView';
import CustomerProfileView from './views/CustomerProfileView';
import NotificationsView from './views/NotificationsView';
import MeetingDetailView from './views/MeetingDetailView';
import SalesMeetingDetailView from './views/SalesMeetingDetailView';
import EditMeetingView from './views/EditMeetingView';
import StaffManagementView from './views/StaffManagementView';
import MeetingRequestsView from './views/MeetingRequestsView';
import NPILinkingView from './views/NPILinkingView';
import ProviderProfileManagementView from './views/ProviderProfileManagementView';
import MeetingConfirmationView from './views/MeetingConfirmationView';
import OrderManagementView from './views/OrderManagementView';
import AddMeetingManuallyView from './views/AddMeetingManuallyView';
import TermsOfServiceView from './views/TermsOfServiceView';
import PrivacyPolicyView from './views/PrivacyPolicyView';
import AdminReferralView from './views/AdminReferralView';
import NPILinking from './utils/npiLinking';
import { logger } from './utils/logger';
import ErrorBoundary from './components/ErrorBoundary';
import { initializeSecurity } from './utils/security';
import versionChecker from './utils/versionChecker';
import { SpeedInsights } from '@vercel/speed-insights/react';
import AdminAccountSwitcher from './components/AdminAccountSwitcher';
import AdminImpersonationBanner from './components/AdminImpersonationBanner';
import { adminImpersonationService } from './utils/adminImpersonationService';
import { StatusBar, Style } from '@capacitor/status-bar';

// API URL for serverless functions - use full URL for native apps (Capacitor)
const API_URL = process.env.REACT_APP_API_URL || '';

export default function App() {
  const [currentView, setCurrentView] = useState('login');
  const [userType, setUserType] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedMeetingId, setSelectedMeetingId] = useState(null);
  const [selectedMeetingDate, setSelectedMeetingDate] = useState(null);
  const [notifications, setNotifications] = useState(0);
  const [saveStatus, setSaveStatus] = useState('');
  const [linkedNPIProfile, setLinkedNPIProfile] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [orientationKey, setOrientationKey] = useState(0); // Forces re-render on orientation change
  const [inviteToken] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [hasValidSubscription, setHasValidSubscription] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [profileData, setProfileData] = useState({
    name: '',
    location: '',
    company: '',
    specialty: '',
    title: '',
    territory: '',
    phone: '',
    license: '',
    products: []
  });

  // Ref to prevent duplicate sign-in handling between initializeAuth and onAuthStateChange
  // Using ref instead of state to avoid stale closure issues in onAuthStateChange callback
  const isAuthInitializedRef = React.useRef(false);

  // Initialize app and check for existing session
  useEffect(() => {
    // Check version and clear old localStorage data if needed
    const versionUpdated = versionChecker.checkAndUpdate();
    if (versionUpdated) {
      logger.debug('App updated - old data cleared');
    }

    // Check for magic link auth callback or password reset
    const pathname = window.location.pathname;
    const hash = window.location.hash;

    // Check for auth callbacks and password reset (supports multiple Supabase redirect URLs)
    if (pathname === '/auth/callback' || pathname === '/auth/reset-password' || pathname === '/recover' || hash.includes('type=recovery')) {
      handleAuthCallback();
      return;
    }

    // Check for payment success/cancel callbacks
    if (pathname === '/payment-success' || pathname === '/payment-canceled') {
      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('session_id');

      if (pathname === '/payment-success' && sessionId) {
        // Payment successful - fetch subscription from server
        logger.debug('Payment successful', { sessionId });
        handlePaymentSuccess(sessionId);
      } else if (pathname === '/payment-canceled') {
        // Payment canceled - show message
        logger.debug('Payment canceled by user');
        // Redirect to dashboard and clear URL
        window.history.replaceState({}, document.title, '/');
        setCurrentView('dashboard');
      }
      return;
    }

    // Check for external meeting confirmation URLs first
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const action = urlParams.get('action');

    if (token && action && ['confirm', 'decline'].includes(action)) {
      setCurrentView('meeting-confirmation');
      return; // Skip normal user session checks for external confirmation
    }

    // Mobile detection
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    // Capacitor native app detection - add class for iOS-specific CSS
    const isCapacitorApp = window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
    if (isCapacitorApp) {
      document.documentElement.classList.add('capacitor-native');
      document.body.classList.add('capacitor-native');
      if (window.Capacitor.getPlatform && window.Capacitor.getPlatform() === 'ios') {
        document.documentElement.classList.add('capacitor-ios');
        document.body.classList.add('capacitor-ios');
        // Configure iOS status bar - light style with white background
        StatusBar.setStyle({ style: Style.Light }).catch(() => {});
        StatusBar.setBackgroundColor({ color: '#ffffff' }).catch(() => {});
      }
    }

    // Handle orientation changes - forces re-render to recalculate safe area insets
    const handleOrientationChange = () => {
      // Small delay to let the browser update safe area insets
      setTimeout(() => {
        setOrientationKey(prev => prev + 1);
        checkMobile();
      }, 100);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    window.addEventListener('orientationchange', handleOrientationChange);

    // Initialize Supabase auth state
    initializeAuth();

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Set up Supabase auth state listener (only if Supabase is available)
  useEffect(() => {
    if (!supabase) {
      return;
    }

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug('Auth state changed:', { event, hasSession: !!session, hasUser: !!session?.user });

      // Check if this is a password recovery session
      const hash = window.location.hash;
      const pathname = window.location.pathname;
      const isRecovery = hash.includes('type=recovery') ||
                         pathname === '/auth/reset-password' ||
                         pathname === '/recover';

      if (isRecovery) {
        logger.debug('Recovery session detected in auth state change, showing password reset screen');
        setCurrentView('password-reset');
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        // Skip if initializeAuth already handled this session to prevent race condition
        if (isAuthInitializedRef.current) {
          logger.debug('Skipping SIGNED_IN handler - auth already initialized by initializeAuth()');
          return;
        }
        await handleUserSignIn(session.user);
      } else if (event === 'SIGNED_OUT') {
        handleUserSignOut();
        isAuthInitializedRef.current = false; // Reset flag on sign out
      }
    });

    return () => {
      authListener.subscription?.unsubscribe();
    };
  }, []);

  // Initialize offline sync manager and security
  useEffect(() => {
    // Initialize security measures
    initializeSecurity();

    // Try to sync any pending operations from previous sessions
    if (navigator.onLine) {
      offlineSyncManager.processSyncQueue();
    }
  }, []);

  // Update notification count when user changes
  useEffect(() => {
    if (currentUser && (currentUser.user_type === 'customer' || currentUser.user_type === 'office_staff')) {
      updateNotificationCount();
    } else {
      setNotifications(0);
    }
  }, [currentUser]);

  // Scroll to top whenever the view changes
  useEffect(() => {
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.scrollTop = 0;
    }
  }, [currentView]);

  const handlePaymentSuccess = async (sessionId) => {
    try {
      logger.debug('Handling payment success, fetching subscription from server');

      // Wait a bit for webhook to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Get current user
      const { user, profile } = await authService.getCurrentUser();

      if (!user || !profile) {
        logger.error('No user found after payment');
        window.history.replaceState({}, document.title, '/');
        setCurrentView('login');
        return;
      }

      // Fetch subscription from server using Vercel serverless function
      const response = await fetch(`${API_URL}/subscription-status?userId=${user.id}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.hasSubscription && (data.subscription.status === 'active' || data.subscription.status === 'trialing')) {
        // Save subscription to localStorage for offline access
        PaymentUtils.saveUserSubscription(user.id, {
          id: data.subscription.id,
          status: data.subscription.status,
          plan_type: data.subscription.planType,
          current_period_start: new Date(data.subscription.currentPeriodStart).getTime(),
          current_period_end: new Date(data.subscription.currentPeriodEnd).getTime(),
          cancel_at_period_end: data.subscription.cancelAtPeriodEnd,
          updated_at: new Date().toISOString()
        });

        logger.debug('Subscription loaded successfully', { plan: data.subscription.planType });

        // Update state
        setHasValidSubscription(true);
        setShowPaywall(false);

        // Redirect to dashboard
        window.history.replaceState({}, document.title, '/');
        setCurrentView('dashboard');
      } else {
        logger.warn('Subscription not active yet, retrying');
        // Retry once more after another delay
        await new Promise(resolve => setTimeout(resolve, 3000));
        const retryResponse = await fetch(`${API_URL}/subscription-status?userId=${user.id}`);

        if (!retryResponse.ok) {
          throw new Error(`HTTP error! status: ${retryResponse.status}`);
        }

        const retryData = await retryResponse.json();

        if (retryData.hasSubscription && (retryData.subscription.status === 'active' || retryData.subscription.status === 'trialing')) {
          PaymentUtils.saveUserSubscription(user.id, {
            id: retryData.subscription.id,
            status: retryData.subscription.status,
            plan_type: retryData.subscription.planType,
            current_period_start: new Date(retryData.subscription.currentPeriodStart).getTime(),
            current_period_end: new Date(retryData.subscription.currentPeriodEnd).getTime(),
            cancel_at_period_end: retryData.subscription.cancelAtPeriodEnd,
            updated_at: new Date().toISOString()
          });

          setHasValidSubscription(true);
          setShowPaywall(false);
          window.history.replaceState({}, document.title, '/');
          setCurrentView('dashboard');
        } else {
          // Still not ready, just redirect to dashboard and let user retry
          logger.warn('Subscription not ready after retries');
          window.history.replaceState({}, document.title, '/');
          setCurrentView('dashboard');
        }
      }
    } catch (error) {
      logger.error('Error handling payment success', { error: error.message });
      // Redirect to dashboard anyway
      window.history.replaceState({}, document.title, '/');
      setCurrentView('dashboard');
    }
  };

  const updateNotificationCount = async () => {
    try {
      if (!currentUser) {
        setNotifications(0);
        return;
      }

      // Debug: Log currentUser to diagnose delegation issues
      logger.debug('updateNotificationCount: currentUser context', {
        userId: currentUser.id,
        userType: currentUser.user_type,
        hasDelegatingFor: !!currentUser.delegatingFor,
        delegatingForId: currentUser.delegatingFor?.id,
        delegatingForName: currentUser.delegatingFor?.name
      });

      // Import services to get actual meeting requests from Supabase
      import('./utils/supabaseData').then(async ({ meetingService, attendeeService }) => {
        try {
          // Determine effective user for delegation scenarios
          let effectiveUserId = currentUser.id;
          let effectiveUserType = currentUser.user_type;

          // If office staff is acting on behalf of physician, use physician's ID
          if (currentUser.user_type === 'office_staff' && currentUser.delegatingFor) {
            effectiveUserId = currentUser.delegatingFor.id;
            effectiveUserType = 'customer';
            logger.debug('updateNotificationCount: Office staff delegating', {
              staffId: currentUser.id,
              physicianId: effectiveUserId,
              physicianName: currentUser.delegatingFor.name
            });
          }

          // Get all meetings for the effective user using same method as Dashboard
          const allMeetings = await meetingService.getAllMeetings(effectiveUserId, 'customer');

          // Filter for pending meetings (same logic as Dashboard)
          const pendingRequests = allMeetings.filter(meeting => meeting.status === 'pending');

          // Set notification count to pending meetings count
          const totalCount = pendingRequests.length;
          setNotifications(totalCount);
          logger.debug('Updated notification count:', {
            effectiveUserId,
            effectiveUserType,
            isDelegated: currentUser.user_type === 'office_staff',
            pendingRequests: pendingRequests.length,
            totalCount
          });
        } catch (supabaseError) {
          logger.warn('Supabase notification count failed, trying localStorage fallback:', supabaseError);

          // Fallback to localStorage
          const { MeetingStorage } = await import('./utils/meetingStorage');
          const requests = MeetingStorage.getMeetingRequestsForProvider(currentUser.id);
          const pendingRequests = requests.filter(request => request.status === 'pending');

          // Set notification count to ONLY pending meetings
          const totalCount = pendingRequests.length;
          setNotifications(totalCount);
          logger.debug('Updated notification count from localStorage fallback:', {
            pendingRequests: pendingRequests.length,
            totalCount
          });
        }
      });
    } catch (error) {
      logger.error('Error updating notification count:', error);
      setNotifications(0);
    }
  };

  // Check if user needs paywall
  const checkSubscriptionStatus = async (user) => {
    logger.debug('checkSubscriptionStatus called', { userId: user?.id, userType: user?.user_type, email: user?.email });
    setIsCheckingSubscription(true);

    if (!user) {
      logger.debug('No user provided to checkSubscriptionStatus');
      setIsCheckingSubscription(false);
      return;
    }

    // Validate user.id exists before making API call
    if (!user.id) {
      logger.error('checkSubscriptionStatus: user.id is missing!');
      setIsCheckingSubscription(false);
      return;
    }

    // Healthcare providers and their office staff get free access
    if (user.user_type === 'customer' || user.user_type === 'office_staff') {
      logger.debug(`${user.user_type === 'customer' ? 'Healthcare provider' : 'Office staff'} - free access granted`);
      setHasValidSubscription(true);
      setShowPaywall(false);
      setIsCheckingSubscription(false);
      return;
    }

    // Demo/Development: Allow demo users to access everything for testing
    // Also grant super admin (ryan@meetwithreps.com) free access
    if (user.email === 'rep@repconnect.com' ||
        user.email === 'ryan@meetwithreps.com' ||
        (user.profile && user.profile.email === 'rep@repconnect.com') ||
        (user.profile && user.profile.email === 'ryan@meetwithreps.com') ||
        (user.user && user.user.email === 'rep@repconnect.com') ||
        (user.user && user.user.email === 'ryan@meetwithreps.com')) {
      logger.debug('Demo user or super admin - free access granted');
      setHasValidSubscription(true);
      setShowPaywall(false);
      setIsCheckingSubscription(false);
      return;
    }

    // Check if sales reps and managers have valid subscriptions
    // First check localStorage for quick access
    const localSub = PaymentUtils.getUserSubscription(user.id);
    logger.debug('Local subscription check', {
      found: !!localSub,
      status: localSub?.status,
      isValid: localSub && (localSub.status === 'active' || localSub.status === 'trialing') && localSub.current_period_end > Date.now()
    });

    if (localSub && (localSub.status === 'active' || localSub.status === 'trialing') && localSub.current_period_end > Date.now()) {
      logger.debug('Valid local subscription found');
      setHasValidSubscription(true);
      setShowPaywall(false);
      setIsCheckingSubscription(false);

      // Verify with server in background
      verifySubscriptionWithServer(user.id);
      return;
    }

    // No valid local subscription, check with server
    // Use retry logic to handle intermittent failures on initial load
    const checkWithRetry = async (retryCount = 0, maxRetries = 2) => {
      const apiUrl = `${API_URL}/subscription-status?userId=${user.id}`;
      logger.debug('Checking subscription with server', { userId: user.id, apiUrl, attempt: retryCount + 1 });

      try {
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

        const response = await fetch(apiUrl, { signal: controller.signal });
        clearTimeout(timeoutId);

        logger.debug('Subscription API response status', { status: response.status, ok: response.ok, attempt: retryCount + 1 });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        logger.debug('Server subscription response', {
          hasSubscription: data.hasSubscription,
          status: data.subscription?.status,
          subscriptionId: data.subscription?.id,
          attempt: retryCount + 1
        });

        return data;
      } catch (error) {
        logger.error('Subscription check failed', { error: error.message, attempt: retryCount + 1 });

        // Retry on failure (but not on abort/timeout for last retry)
        if (retryCount < maxRetries) {
          logger.debug('Retrying subscription check after delay...', { nextAttempt: retryCount + 2 });
          await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1))); // Exponential backoff
          return checkWithRetry(retryCount + 1, maxRetries);
        }
        throw error;
      }
    };

    try {
      const data = await checkWithRetry();

      if (data.hasSubscription && (data.subscription.status === 'active' || data.subscription.status === 'trialing')) {
        logger.debug('Active/trialing subscription found on server, saving to localStorage');
        // Save to localStorage
        PaymentUtils.saveUserSubscription(user.id, {
          id: data.subscription.id,
          status: data.subscription.status,
          plan_type: data.subscription.planType,
          current_period_start: new Date(data.subscription.currentPeriodStart).getTime(),
          current_period_end: new Date(data.subscription.currentPeriodEnd).getTime(),
          cancel_at_period_end: data.subscription.cancelAtPeriodEnd,
          updated_at: new Date().toISOString()
        });

        setHasValidSubscription(true);
        setShowPaywall(false);
      } else {
        logger.debug('No valid subscription found after all attempts');
        // No valid subscription
        setHasValidSubscription(false);
        const paymentRequired = PaymentUtils.isPaymentRequired(user.user_type);
        logger.debug('Payment required', { paymentRequired, userType: user.user_type });
        setShowPaywall(paymentRequired);
      }
    } catch (error) {
      logger.error('Error checking subscription status after all retries', { error: error.message });
      // Fallback to localStorage check
      const hasValidSub = PaymentUtils.hasValidSubscription(user);
      logger.debug('Fallback localStorage check', { hasValidSub });
      setHasValidSubscription(hasValidSub);

      if (!hasValidSub && PaymentUtils.isPaymentRequired(user.user_type)) {
        logger.debug('Showing paywall after all retries failed');
        setShowPaywall(true);
      }
    } finally {
      setIsCheckingSubscription(false);
    }
  };

  // Verify subscription with server in background
  const verifySubscriptionWithServer = async (userId) => {
    logger.debug('Background verification starting', { userId });
    try {
      const response = await fetch(`${API_URL}/subscription-status?userId=${userId}`);

      logger.debug('Background verification response', { status: response.status });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      logger.debug('Background verification data', { hasSubscription: data.hasSubscription, status: data.subscription?.status });

      if (data.hasSubscription && (data.subscription.status === 'active' || data.subscription.status === 'trialing')) {
        logger.debug('Background verification: Subscription still valid (active or trialing)');
        // Update localStorage with latest data
        PaymentUtils.saveUserSubscription(userId, {
          id: data.subscription.id,
          status: data.subscription.status,
          plan_type: data.subscription.planType,
          current_period_start: new Date(data.subscription.currentPeriodStart).getTime(),
          current_period_end: new Date(data.subscription.currentPeriodEnd).getTime(),
          cancel_at_period_end: data.subscription.cancelAtPeriodEnd,
          updated_at: new Date().toISOString()
        });
      } else {
        // Server says subscription is cancelled/inactive - trust the server
        logger.warn('Background verification: Subscription no longer valid', {
          hasSubscription: data.hasSubscription,
          serverStatus: data.subscription?.status
        });

        // Clear localStorage cache
        PaymentUtils.clearUserSubscription(userId);

        // Show paywall since subscription is no longer valid
        setHasValidSubscription(false);
        setShowPaywall(true);
      }
    } catch (error) {
      // Silent fail - we already have a valid local subscription
      logger.warn('Background subscription verification failed', { error: error.message });
    }
  };

  // Handle successful subscription
  const handleSubscriptionSuccess = (subscription) => {
    setHasValidSubscription(true);
    setShowPaywall(false);
    
    // Refresh the current view or redirect to dashboard
    if (currentView === 'dashboard' || currentView === 'onboarding') {
      setCurrentView('dashboard');
    }
  };

  // Check if user can access a specific view
  const canAccessView = (viewName) => {
    if (!currentUser) {
      return false;
    }

    // Healthcare providers and office staff can access everything (they get free access)
    if (currentUser.user_type === 'customer' || currentUser.user_type === 'office_staff') {
      logger.debug(`canAccessView: ${currentUser.user_type === 'customer' ? 'Healthcare provider' : 'Office staff'} - access granted`);
      return true;
    }

    // Settings is always accessible
    if (viewName === 'settings') {
      logger.debug('canAccessView: Settings always accessible');
      return true;
    }

    // Login, onboarding, and meeting-confirmation are always accessible
    if (['login', 'onboarding', 'meeting-confirmation'].includes(viewName)) {
      logger.debug('canAccessView: Public view - access granted');
      return true;
    }

    // Demo/Development: Allow demo users to access everything for testing
    if (currentUser.email === 'rep@repconnect.com' ||
        (currentUser.profile && currentUser.profile.email === 'rep@repconnect.com') ||
        (currentUser.user && currentUser.user.email === 'rep@repconnect.com')) {
      logger.debug('canAccessView: Demo user - access granted');
      return true;
    }

    // If still checking subscription, allow access temporarily (prevents false paywall during load)
    if (isCheckingSubscription) {
      logger.debug('canAccessView: Still checking subscription, allowing temporary access');
      return true;
    }

    // For sales reps and managers, check subscription
    logger.debug('canAccessView: Subscription check result', { hasValidSubscription });
    return hasValidSubscription;
  };

  // Handle navigation with subscription checking
  const handleNavigation = (newView) => {
    if (canAccessView(newView)) {
      setCurrentView(newView);
      // Scroll to top when navigating - target #root since body is fixed
      // Use setTimeout to ensure React has rendered the new view first
      setTimeout(() => {
        const rootElement = document.getElementById('root');
        if (rootElement) {
          rootElement.scrollTo({ top: 0, behavior: 'instant' });
        }
      }, 0);
    } else {
      // Show paywall if user tries to access a restricted view
      setShowPaywall(true);
    }
  };

  // Get the effective user for delegation scenarios
  // Office staff acting on behalf of physician should use physician's data
  const getEffectiveUser = (user) => {
    if (!user) return null;

    if (user.user_type === 'office_staff' && user.delegatingFor) {
      return {
        ...user.delegatingFor,
        actualUserType: 'office_staff',
        staffId: user.id,
        isActingOnBehalf: true
      };
    }

    return user;
  };

  const initializeAuth = async () => {
    const { user, profile } = await authService.getCurrentUser();
    if (user && profile) {
      // Mark auth as initialized FIRST to prevent onAuthStateChange race condition
      isAuthInitializedRef.current = true;

      const mergedUser = { ...user, ...profile };

      // Debug: Log user context on initialization
      logger.debug('initializeAuth: User loaded', {
        userId: mergedUser.id,
        userType: mergedUser.user_type,
        hasDelegatingFor: !!mergedUser.delegatingFor,
        delegatingForId: mergedUser.delegatingFor?.id,
        delegatingForName: mergedUser.delegatingFor?.name
      });

      if (!profile.onboarding_completed) {
        setCurrentUser(mergedUser);
        setCurrentView('onboarding');
      } else {
        setUserType(profile.user_type);

        // Check subscription status BEFORE setting user/view to prevent Dashboard from rendering prematurely
        await checkSubscriptionStatus({ ...user, ...profile });

        // Only set currentUser AFTER subscription check to prevent Dashboard from rendering prematurely
        setCurrentUser(mergedUser);
        setCurrentView('dashboard');
        updateProfileData(profile);

        // Load linked NPI profile for healthcare providers
        if (profile.user_type === 'customer') {
          const npiLink = NPILinking.getUserNPILink(user.id);
          if (npiLink) {
            setLinkedNPIProfile(npiLink.npiProfile);
          }
        }
      }
    }
  };

  const handleAuthCallback = async () => {
    if (!supabase) {
      logger.error('Supabase not configured for auth callback');
      setCurrentView('login');
      return;
    }

    try {
      // Check if this is a password recovery flow
      const hash = window.location.hash;
      const pathname = window.location.pathname;
      if (hash.includes('type=recovery') || pathname === '/auth/reset-password' || pathname === '/recover') {
        setCurrentView('password-reset');
        // DON'T clear the URL yet - the hash contains the recovery token needed by Supabase
        // The PasswordResetScreen component will handle the token
        return;
      }

      const { data, error } = await supabase.auth.getSession();

      if (error) {
        logger.error('Auth callback error:', error);
        setCurrentView('login');
        return;
      }

      if (data.session && data.session.user) {
        await handleUserSignIn(data.session.user);
        // Clean up URL
        window.history.replaceState({}, document.title, '/');
      } else {
        setCurrentView('login');
      }
    } catch (error) {
      logger.error('Auth callback processing error:', error);
      setCurrentView('login');
    }
  };

  const handleUserSignIn = async (user) => {
    const profile = await authService.getUserProfile(user.id);
    if (profile) {
      const mergedUser = { ...user, ...profile };

      // Debug: Log user context on sign in
      logger.debug('handleUserSignIn: User signed in', {
        userId: mergedUser.id,
        userType: mergedUser.user_type,
        hasDelegatingFor: !!mergedUser.delegatingFor,
        delegatingForId: mergedUser.delegatingFor?.id,
        delegatingForName: mergedUser.delegatingFor?.name
      });

      if (!profile.onboarding_completed) {
        setCurrentUser(mergedUser);
        setCurrentView('onboarding');
      } else {
        setUserType(profile.user_type);

        // Check subscription status BEFORE setting user/view to prevent false paywall flash
        await checkSubscriptionStatus({ ...user, ...profile });

        // Only set currentUser AFTER subscription check to prevent Dashboard from rendering prematurely
        setCurrentUser(mergedUser);
        setCurrentView('dashboard');
        updateProfileData(profile);
      }
    }
  };

  const handleUserSignOut = () => {
    setCurrentUser(null);
    setUserType('');
    setCurrentView('login');
    setProfileData({
      name: '',
      location: '',
      company: '',
      specialty: '',
      title: '',
      territory: '',
      phone: '',
      license: '',
      products: []
    });
  };

  const updateProfileData = (profile) => {
    setProfileData({
      name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
      location: '',
      company: profile.company || '',
      specialty: '',
      title: '',
      territory: profile.territory || '',
      phone: profile.phone || '',
      license: '',
      products: []
    });
  };

  // Logout function
  const handleLogout = async () => {
    try {
      await authService.signOut();
      // Since we're using localStorage fallback, auth state listener won't fire
      // So we need to manually call handleUserSignOut
      handleUserSignOut();
    } catch (error) {
      logger.error('Logout error:', error);
      // Fallback - force logout on client side
      handleUserSignOut();
    }
  };

  // Admin Impersonation Handler
  const handleSwitchAccount = async (targetUserId) => {
    try {
      if (!targetUserId) {
        // Exit impersonation - return to admin account
        logger.info('Exiting impersonation');
        const adminUser = await adminImpersonationService.endImpersonation();
        setCurrentUser(adminUser);
        setUserType(adminUser.user_type);
        updateProfileData(adminUser);
        setCurrentView('dashboard');
        return;
      }

      // Start impersonation
      logger.info('Starting impersonation', { targetUserId });
      const targetUser = await adminImpersonationService.startImpersonation(targetUserId, currentUser);
      setCurrentUser(targetUser);
      setUserType(targetUser.user_type);
      updateProfileData(targetUser);
      setCurrentView('dashboard');
    } catch (error) {
      logger.error('Failed to switch account', { error: error.message });
      alert(`Failed to switch account: ${error.message}`);
    }
  };

  // Mobile View Renderer
  const renderMobileView = () => {
    switch (currentView) {
      case 'dashboard':
        if (userType === 'sales') {
          // Use full Desktop dashboard on mobile for familiarity
          return (
            <Dashboard
              userType={userType}
              profileData={profileData}
              setCurrentView={handleNavigation}
              setSelectedMeetingId={setSelectedMeetingId}
              setSelectedMeetingDate={setSelectedMeetingDate}
              hasValidSubscription={hasValidSubscription}
              currentUser={currentUser}
              updateNotificationCount={updateNotificationCount}
            />
          );
        }
        // Office staff uses the same customer dashboard but with delegation context
        if (userType === 'office_staff') {
          return (
            <Dashboard
              userType="customer" // Show customer interface
              profileData={profileData}
              setCurrentView={handleNavigation}
              setSelectedMeetingId={setSelectedMeetingId}
              setSelectedMeetingDate={setSelectedMeetingDate}
              delegatingFor={currentUser.delegatingFor} // Show who they're managing for
              isDelegated={true}
              hasValidSubscription={hasValidSubscription}
              currentUser={currentUser}
              updateNotificationCount={updateNotificationCount}
            />
          );
        }
        return (
          <Dashboard
            userType={userType}
            profileData={profileData}
            setCurrentView={handleNavigation}
            setSelectedMeetingId={setSelectedMeetingId}
            setSelectedMeetingDate={setSelectedMeetingDate}
            hasValidSubscription={hasValidSubscription}
            currentUser={currentUser}
            updateNotificationCount={updateNotificationCount}
          />
        );
      
      case 'find':
        return (
          <MobileProviderSearch 
            setCurrentView={handleNavigation}
            setSelectedCustomer={setSelectedCustomer}
          />
        );
        
      case 'book-meeting':
        return (
          <MobileBookingFlow 
            selectedCustomer={selectedCustomer}
            setCurrentView={handleNavigation}
          />
        );
        
      case 'meetings':
        return (
          <MobileCalendar 
            setCurrentView={handleNavigation}
            setSelectedMeetingId={setSelectedMeetingId}
          />
        );
        
      case 'meeting-detail':
        if (userType === 'sales') {
          return (
            <SalesMeetingDetailView
              meetingId={selectedMeetingId}
              setCurrentView={handleNavigation}
              setSelectedMeetingId={setSelectedMeetingId}
              currentUser={currentUser}
            />
          );
        }
        return (
          <MeetingDetailView
            meetingId={selectedMeetingId}
            setCurrentView={handleNavigation}
            setSelectedMeetingId={setSelectedMeetingId}
            currentUser={currentUser}
            updateNotificationCount={updateNotificationCount}
          />
        );
        
      case 'edit-meeting':
        return (
          <EditMeetingView
            meetingId={selectedMeetingId}
            setCurrentView={handleNavigation}
            currentUser={currentUser}
            onMeetingUpdated={(updatedMeeting) => {

            }}
          />
        );
        
      case 'settings':
        return <SettingsView
          userType={userType}
          currentUser={currentUser}
          setCurrentUser={setCurrentUser}
          profileData={profileData}
          setProfileData={setProfileData}
          saveStatus={saveStatus}
          setSaveStatus={setSaveStatus}
          onLogout={handleLogout}
        />;
        
      case 'notifications':
        return <NotificationsView userType={userType} currentUser={currentUser} />;
        
      case 'availability':
        return <AvailabilityView currentUser={currentUser} />;

      case 'customer-locations':
        return <CustomerLocationsView currentUser={currentUser} />;

      case 'dining-preferences':
        return <DiningPreferencesView currentUser={currentUser} />;

      case 'team-management':
        return <TeamManagement currentUser={currentUser} />;

      case 'meeting-requests':

        return <MeetingRequestsView currentUser={currentUser} setCurrentView={handleNavigation} setSelectedMeetingId={setSelectedMeetingId} updateNotificationCount={updateNotificationCount} selectedMeetingDate={selectedMeetingDate} setSelectedMeetingDate={setSelectedMeetingDate} />;
        
      case 'staff-management':
        return <StaffManagementView />;
        
      default:
        return (
          <div className="p-4 text-center">
            <p className="text-gray-600">View not found</p>
            <button 
              onClick={() => setCurrentView('dashboard')}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              Go to Dashboard
            </button>
          </div>
        );
    }
  };

  const renderView = () => {
    if (currentView === 'login') {
      return <AuthScreen
        setUserType={setUserType}
        setCurrentView={handleNavigation}
        setCurrentUser={setCurrentUser}
      />;
    }

    if (currentView === 'password-reset') {
      return <PasswordResetScreen
        setCurrentView={handleNavigation}
      />;
    }

    if (currentView === 'terms-of-service') {
      return <TermsOfServiceView
        setCurrentView={handleNavigation}
      />;
    }

    if (currentView === 'privacy-policy') {
      return <PrivacyPolicyView
        setCurrentView={handleNavigation}
      />;
    }

    if (currentView === 'admin-login') {
      return <AdminLogin 
        onLogin={(user) => {
          setCurrentUser(user);
          setUserType(user.userType);
          setCurrentView('dashboard');
        }}
      />;
    }
    
    if (currentView === 'admin-invite') {
      return <AdminInviteAcceptance 
        inviteToken={inviteToken}
        onSuccess={() => {
          setCurrentView('admin-login');
        }}
      />;
    }
    
    if (currentView === 'onboarding') {
      return <OnboardingFlow
        currentUser={currentUser}
        setCurrentView={handleNavigation}
        setUserType={setUserType}
        setCurrentUser={setCurrentUser}
        onOnboardingComplete={(user) => {
          // Check subscription status after onboarding completion
          checkSubscriptionStatus(user);
        }}
      />;
    }
    
    if (currentView === 'meeting-confirmation') {
      return <MeetingConfirmationView />;
    }
    
    // Mobile Layout - All users now use desktop interface for consistency
    // Disabled mobile-specific layout to provide uniform experience
    // if (isMobile && userType !== 'sales') {
    //   return (
    //     <div className="min-h-screen bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100">
    //       {/* Mobile Views */}
    //       <div className="pb-16"> {/* Space for bottom navigation */}
    //         {renderMobileView()}
    //       </div>
    //
    //       {/* Mobile Bottom Navigation */}
    //       <MobileNavigation
    //         userType={userType}
    //         currentView={currentView}
    //         setCurrentView={handleNavigation}
    //         notifications={notifications}
    //       />
    //     </div>
    //   );
    // }

    // Main App Layout - Responsive with bottom tab nav on mobile
    return (
      <div className="flex h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 overflow-x-hidden">
        {/* Desktop Sidebar - Hidden on mobile */}
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          userType={userType}
          currentView={currentView}
          setCurrentView={handleNavigation}
          setUserType={setUserType}
          notifications={notifications}
          onLogout={handleLogout}
          currentUser={currentUser}
          linkedNPIProfile={linkedNPIProfile}
          onSwitchAccount={handleSwitchAccount}
        />
        <div id="app-scroll-container" className="flex-1 overflow-auto">
          {/* Scroll anchor for navigation - invisible element at very top */}
          <div id="scroll-anchor" style={{ position: 'absolute', top: 0, left: 0, height: 0, width: 0 }} />
          {/* Admin Impersonation Banner - Shows at very top when impersonating */}
          <AdminImpersonationBanner
            currentUser={currentUser}
            onExitImpersonation={() => handleSwitchAccount(null)}
          />

          {/* Mobile Header - Simplified for iOS with safe area */}
          {/* orientationKey forces remount on orientation change to recalculate safe area insets */}
          <header
            key={`mobile-header-${orientationKey}`}
            className={`fixed left-0 right-0 bg-white shadow-sm lg:hidden z-40 ${
              adminImpersonationService.isImpersonating() ? 'top-[60px]' : 'top-0'
            }`}
            style={{
              paddingTop: 'env(safe-area-inset-top, 0px)',
              paddingLeft: '12px',
              paddingRight: '12px'
            }}
          >
            {/* Inner container for header content - fixed height */}
            <div className="flex items-center justify-between" style={{ height: '44px' }}>
            <div className="flex items-center">
              {/* Hamburger menu for accessing full sidebar options */}
              <button
                onClick={() => setSidebarOpen(true)}
                onTouchEnd={(e) => { e.preventDefault(); setSidebarOpen(true); }}
                className="p-2 -ml-1 touch-manipulation rounded-lg hover:bg-gray-100 active:bg-gray-200"
                aria-label="Open menu"
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
            </div>
            <h1 className="text-lg font-semibold text-gray-900">RepConnect</h1>
            <div className="flex items-center">
              {/* Admin Account Switcher (only visible to super admin) */}
              <AdminAccountSwitcher
                currentUser={currentUser}
                onSwitchAccount={handleSwitchAccount}
              />
              {/* Spacer to balance header when no admin switcher */}
              {currentUser?.email !== 'ryan@meetwithreps.com' && (
                <div style={{ width: '44px' }}></div>
              )}
            </div>
            </div>
          </header>

          {/* Main Content Area - with padding for mobile header and bottom nav */}
          {/* orientationKey forces remount on orientation change to recalculate safe area insets */}
          <main
            key={`main-content-${orientationKey}`}
            className="lg:pt-0"
            style={{
              paddingTop: isMobile
                ? adminImpersonationService.isImpersonating()
                  ? 'calc(104px + env(safe-area-inset-top, 0px))'
                  : 'calc(44px + env(safe-area-inset-top, 0px))'
                : '0',
              paddingBottom: isMobile ? 'calc(49px + env(safe-area-inset-bottom, 0px))' : '0'
            }}
          >
            {/* Block access to restricted views for non-subscribers */}
            {!canAccessView(currentView) ? (
              <div className="p-6">
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coffee className="w-8 h-8 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">Subscription Required</h3>
                  <p className="text-gray-600 mb-4">You need an active subscription to access this feature.</p>
                  <div className="space-y-2">
                    <button
                      onClick={() => setShowPaywall(true)}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                    >
                      View Subscription Plans
                    </button>
                    <br />
                    <button
                      onClick={() => handleNavigation('settings')}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Go to Settings
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
            {currentView === 'dashboard' && userType === 'manager' && (
              <ManagerDashboard 
                setCurrentView={handleNavigation}
                setSelectedRepId={(repId) => {
                  // Handle rep detail view if needed

                }}
              />
            )}
            
            {currentView === 'dashboard' && userType === 'admin' && (
              <AdminDashboard 
                currentUser={currentUser}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
              />
            )}
            
            {currentView === 'dashboard' && userType !== 'manager' && userType !== 'admin' && (
              <Dashboard
                userType={userType === 'office_staff' ? 'customer' : userType}
                profileData={profileData}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
                setSelectedMeetingDate={setSelectedMeetingDate}
                delegatingFor={currentUser?.delegatingFor}
                isDelegated={userType === 'office_staff'}
                hasValidSubscription={hasValidSubscription}
                currentUser={currentUser}
                effectiveUser={getEffectiveUser(currentUser)}
                updateNotificationCount={updateNotificationCount}
              />
            )}
            
            {currentView === 'find' && (
              <FindProvidersView 
                setSelectedCustomer={setSelectedCustomer}
                setCurrentView={handleNavigation}
                currentUser={currentUser}
                hasValidSubscription={hasValidSubscription}
              />
            )}
            
            {currentView === 'book-meeting' && (
              <BookMeetingView
                selectedCustomer={selectedCustomer}
                setCurrentView={handleNavigation}
                currentUser={currentUser}
                hasValidSubscription={hasValidSubscription}
              />
            )}
            
            {currentView === 'meetings' && (
              <MeetingsView
                currentUser={getEffectiveUser(currentUser)}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
                selectedMeetingDate={selectedMeetingDate}
                setSelectedMeetingDate={setSelectedMeetingDate}
                setSelectedCustomer={setSelectedCustomer}
              />
            )}

            {currentView === 'add-meeting-manually' && (userType === 'customer' || userType === 'office_staff') && (
              <AddMeetingManuallyView
                currentUser={getEffectiveUser(currentUser)}
                setCurrentView={handleNavigation}
                preSelectedDate={selectedMeetingDate}
              />
            )}

            {currentView === 'order-management' && userType === 'sales' && (
              <OrderManagementView
                currentUser={currentUser}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
              />
            )}

            {currentView === 'availability' && (
              <AvailabilityView currentUser={getEffectiveUser(currentUser)} />
            )}

            {currentView === 'customer-locations' && (userType === 'customer' || userType === 'office_staff') && (
              <CustomerLocationsView currentUser={getEffectiveUser(currentUser)} />
            )}

            {(currentView === 'preferences' || currentView === 'dining-preferences') && (userType === 'customer' || userType === 'office_staff') && (
              <DiningPreferencesView currentUser={getEffectiveUser(currentUser)} />
            )}
            
            {currentView === 'staff' && (userType === 'customer' || userType === 'office_staff') && (
              <StaffManagementView currentUser={getEffectiveUser(currentUser)} />
            )}
            
            {currentView === 'requests' && (userType === 'customer' || userType === 'office_staff') && (
              <MeetingRequestsView
                currentUser={getEffectiveUser(currentUser)}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
                updateNotificationCount={updateNotificationCount}
                selectedMeetingDate={selectedMeetingDate}
                setSelectedMeetingDate={setSelectedMeetingDate}
              />
            )}

            {currentView === 'requests' && userType !== 'customer' && userType !== 'office_staff' && (
              <div className="p-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-2">Debug Info</h3>
                  <p className="text-yellow-600">App userType: "{userType}" | Expected: "customer" or "office_staff"</p>
                  <p className="text-yellow-600">CurrentUser type: "{currentUser?.user_type || currentUser?.userType || 'undefined'}"</p>
                </div>
              </div>
            )}

            {/* Compliance Reports - Hidden for now, using placeholder instead */}
            {/* {currentView === 'reports' && (
              <ComplianceReportsView />
            )} */}

            {currentView === 'settings' && (
              <SettingsView
                userType={userType}
                profileData={profileData}
                setProfileData={setProfileData}
                saveStatus={saveStatus}
                setSaveStatus={setSaveStatus}
                currentUser={currentUser}
                setCurrentUser={setCurrentUser}
              />
            )}
            
            {currentView === 'history' && (userType === 'customer' || userType === 'office_staff') && (
              <MeetingHistoryView
                currentUser={getEffectiveUser(currentUser)}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
              />
            )}

            {currentView === 'customer-profile' && (
              <CustomerProfileView
                selectedCustomer={selectedCustomer}
                setCurrentView={handleNavigation}
              />
            )}

            {currentView === 'notifications' && (
              <NotificationsView
                userType={userType === 'office_staff' ? 'customer' : userType}
                currentUser={getEffectiveUser(currentUser)} />
            )}

            {currentView === 'meeting-detail' && (userType === 'customer' || userType === 'office_staff') && (
              <MeetingDetailView
                meetingId={selectedMeetingId}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
                currentUser={getEffectiveUser(currentUser)}
              />
            )}
            
            {currentView === 'meeting-detail' && userType === 'sales' && (
              <SalesMeetingDetailView
                meetingId={selectedMeetingId}
                setCurrentView={handleNavigation}
                setSelectedMeetingId={setSelectedMeetingId}
                currentUser={currentUser}
              />
            )}

            {currentView === 'edit-meeting' && (
              <EditMeetingView
                meetingId={selectedMeetingId}
                setCurrentView={handleNavigation}
                currentUser={getEffectiveUser(currentUser)}
                onMeetingUpdated={(updatedMeeting) => {

                }}
              />
            )}
            
            {currentView === 'link-npi' && userType === 'customer' && (
              <NPILinkingView 
                currentUser={currentUser}
                onLinkingComplete={(npiLink) => {
                  setLinkedNPIProfile(npiLink.npiProfile);
                  setCurrentView('profile-management');
                }}
                setCurrentView={handleNavigation}
              />
            )}
            
            {currentView === 'profile-management' && userType === 'customer' && (
              <ProviderProfileManagementView 
                currentUser={currentUser}
                linkedNPIProfile={linkedNPIProfile}
                setCurrentView={handleNavigation}
              />
            )}
            
            {currentView === 'admin-management' && userType === 'customer' && (
              <AdminManagement 
                currentUser={currentUser}
              />
            )}
            
            {currentView === 'team-management' && (userType === 'customer' || userType === 'office_staff') && (
              <TeamManagement
                currentUser={getEffectiveUser(currentUser)}
              />
            )}

            {/* Admin Referral Leaderboard - Super Admin Only */}
            {currentView === 'admin-referrals' && currentUser?.email === 'ryan@meetwithreps.com' && (
              <AdminReferralView
                currentUser={currentUser}
              />
            )}

            {/* Placeholder views for remaining components */}
            {(
              ['orders', 'reports', 'workflow-analytics'].includes(currentView) ||
              (currentView === 'history' && userType !== 'customer') ||
              (currentView === 'preferences' && userType === 'sales')
            ) && (
              <div className="p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-4">
                  {currentView.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </h2>
                <div className="bg-white rounded-lg shadow-md p-8 text-center">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Coffee className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600">This feature is being developed and will be available soon.</p>
                  <button
                    onClick={() => setCurrentView('dashboard')}
                    className="mt-4 text-blue-600 hover:underline"
                  >
                    Return to Dashboard
                  </button>
                </div>
              </div>
            )}
            </>
            )}

            {/* Copyright Footer - Hidden on mobile where we have bottom nav */}
            <div className="mt-auto py-4 px-6 border-t border-gray-200 bg-white hidden lg:block">
              <p className="text-center text-xs text-gray-400">
                © {new Date().getFullYear()} RepConnect, LLC and meetwithreps.com
              </p>
            </div>
          </main>

          {/* Mobile Bottom Tab Navigation - Shows on mobile only */}
          {isMobile && (
            <MobileNavigation
              userType={userType === 'office_staff' ? 'customer' : userType}
              currentView={currentView}
              setCurrentView={handleNavigation}
              notifications={notifications}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <ErrorBoundary>
      {renderView()}

      {/* Paywall Popup */}
      {showPaywall && (
        <PaywallPopup
          currentUser={currentUser}
          onSubscribe={handleSubscriptionSuccess}
          onClose={() => setShowPaywall(false)}
          allowClose={false} // Don't allow closing without subscribing
        />
      )}

      {/* PWA Install Prompts */}
      <InstallPWA currentUser={currentUser} />
      <IOSInstallPrompt currentUser={currentUser} />

      {/* Vercel Speed Insights */}
      <SpeedInsights />
    </ErrorBoundary>
  );
}